import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// Admin user IDs who bypass feature gates
const ADMIN_USER_IDS = [
  'cf475cb1-592a-4e53-a85f-ea966852b660', // Trey Fleming
  '6b8f58b8-0e2a-40b7-9986-b9905f7e2b8a', // Enrique Barrantes
];

interface FeatureFlags {
  isCreationEnabled: boolean;
  isCompositionEnabled: boolean;
  isConsumptionEnabled: boolean;
  isAdmin: boolean;
  isLoading: boolean;
}

export const useFeatureFlags = (): FeatureFlags => {
  const { user } = useAuth();
  
  const isAdmin = user ? ADMIN_USER_IDS.includes(user.id) : false;

  const { data: flags, isLoading } = useQuery({
    queryKey: ['feature-flags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('flag_name, enabled')
        .in('flag_name', ['bt1_creation_active', 'bt2_composition_active', 'bt3_consumption_active']);
      
      if (error) throw error;
      
      const flagMap: Record<string, boolean> = {};
      data?.forEach(flag => {
        flagMap[flag.flag_name] = flag.enabled;
      });
      
      return flagMap;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  return {
    // Admins bypass all feature gates
    isCreationEnabled: isAdmin || (flags?.bt1_creation_active ?? true),
    isCompositionEnabled: isAdmin || (flags?.bt2_composition_active ?? false),
    isConsumptionEnabled: isAdmin || (flags?.bt3_consumption_active ?? false),
    isAdmin,
    isLoading,
  };
};
